import { useCallback, useState } from "react";

import { AnalyticsFlowEvent } from "@/analytics/types";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { useFlow } from "@/contexts/FlowContext";
import { ActionCard } from "./ActionCard";
import { useComputedValues } from "@/contexts/ComputedValuesContext";
import { useModel } from "@/contexts/ModelContext";
import { useCurrentMedia, useQrProcessingEffect } from "@/store";
import { FileUploadModal } from "@/components/file/UploadModal/FileUploadModal";
import { useTestWithMoreFiles } from "@/useTestWithMoreFiles";

export const TestWithMoreFilesActionCard = () => {
    const { track } = useAnalytics();
    const { task } = useFlow();
    const { isTestingNewFiles, isDownloading } = useModel();
    const currentMedia = useCurrentMedia();
    const { hasEnoughDataToDeploy } = useComputedValues();
    const { testWithMoreFiles } = useTestWithMoreFiles();

    const [isModalOpen, setIsModalOpen] = useState(false);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    // Memoized QR processing callbacks
    const handleQrFileProcessed = useCallback(
        async (file: File) => {
            console.log(
                "🧪 TestWithMoreFilesActionCard: Processing QR file for testing:",
                file.name
            );
            await testWithMoreFiles([file]);
            closeModal();
        },
        [testWithMoreFiles, closeModal]
    );

    const handleQrAnalytics = useCallback(
        (event: string, data: any) => {
            track({
                event: AnalyticsFlowEvent.FILE_UPLOADER_CLICKED,
                data: { ...data, source: "qr_code" }
            });
        },
        [track]
    );

    // QR processing - uses the same shared logic as file input
    useQrProcessingEffect({
        enabled: !isDownloading && !isTestingNewFiles, // enabled when not downloading and not already testing
        onFileProcessed: handleQrFileProcessed,
        onAnalytics: handleQrAnalytics
    });

    const handleTestOnOtherFiles = useCallback(() => {
        track({ event: AnalyticsFlowEvent.TEST_MODEL_CLICKED, data: { task } });
        setIsModalOpen(true);
    }, [track, task]);

    const onAddMediaItems = useCallback(
        async (files: File[]) => {
            await testWithMoreFiles(files);
            closeModal();
        },
        [testWithMoreFiles, closeModal]
    );

    return (
        <>
            <ActionCard
                title={
                    currentMedia?.file.isVideo ||
                    (hasEnoughDataToDeploy && currentMedia?.file.fileType === "image")
                        ? "Test on More Files"
                        : "Upload More Data"
                }
                description={
                    currentMedia?.file.isVideo ||
                    (hasEnoughDataToDeploy && currentMedia?.file.fileType === "image")
                        ? undefined
                        : "Upload data from your computer or phone."
                }
                icon="far fa-add"
                /* iconComponent={
                    currentMedia?.file.fileType === "image" ? undefined : (
                        <QrUploadWidget size={60} />
                    )
                } */
                type={
                    !(
                        currentMedia?.file.isVideo ||
                        (hasEnoughDataToDeploy && currentMedia?.file.fileType === "image")
                    )
                        ? "primary"
                        : "secondary"
                }
                onClick={isDownloading || isTestingNewFiles ? undefined : handleTestOnOtherFiles}
                className={
                    isDownloading || isTestingNewFiles ? "pointer-events-none opacity-50" : ""
                }
            />

            {isModalOpen && (
                <FileUploadModal
                    title="Test on More Files"
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                    onAddMediaItems={onAddMediaItems}
                />
            )}
        </>
    );
};
